import { Node, mergeAttributes } from '@tiptap/core'

export type CalloutType = 'warning' | 'info' | 'success' | 'error'

export interface CalloutOptions {
  HTMLAttributes: Record<string, unknown>
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    callout: {
      setCallout: (attributes: { type: CalloutType }) => ReturnType
      toggleCallout: (attributes: { type: CalloutType }) => ReturnType
      unsetCallout: () => ReturnType
    }
  }
}

// Renk tanımları - email için inline style kullanılacak
const calloutStyles: Record<CalloutType, { bg: string; border: string; text: string }> = {
  warning: {
    bg: '#FEF3C7',
    border: '#F59E0B',
    text: '#92400E'
  },
  info: {
    bg: '#DBEAFE',
    border: '#3B82F6',
    text: '#1E40AF'
  },
  success: {
    bg: '#D1FAE5',
    border: '#10B981',
    text: '#065F46'
  },
  error: {
    bg: '#FEE2E2',
    border: '#EF4444',
    text: '#991B1B'
  }
}

export const Callout = Node.create<CalloutOptions>({
  name: 'callout',

  addOptions() {
    return {
      HTMLAttributes: {},
    }
  },

  group: 'block',

  content: 'block+',

  defining: true,

  addAttributes() {
    return {
      type: {
        default: 'info',
        parseHTML: element => element.getAttribute('data-callout-type') || 'info',
        renderHTML: attributes => {
          return {
            'data-callout-type': attributes.type,
          }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-callout]',
      },
    ]
  },

  renderHTML({ node, HTMLAttributes }) {
    const type = node.attrs.type as CalloutType
    const colors = calloutStyles[type] || calloutStyles.info

    // Email için inline style
    const inlineStyle = `
      background-color: ${colors.bg};
      border-left: 4px solid ${colors.border};
      color: ${colors.text};
      padding: 10px 14px;
      border-radius: 6px;
      margin: 8px 0;
    `.replace(/\s+/g, ' ').trim()

    return [
      'div',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-callout': '',
        'data-callout-type': type,
        class: `callout callout-${type}`,
        style: inlineStyle,
      }),
      0,
    ]
  },

  addCommands() {
    return {
      setCallout:
        (attributes) =>
        ({ commands }) => {
          return commands.wrapIn(this.name, attributes)
        },
      toggleCallout:
        (attributes) =>
        ({ commands, state, chain, tr, dispatch }) => {
          const { from, to } = state.selection

          // Callout içinde miyiz kontrol et
          let depth = state.doc.resolve(from).depth
          let isInsideCallout = false
          let currentCalloutType: string | null = null

          for (let i = depth; i >= 0; i--) {
            const node = state.doc.resolve(from).node(i)
            if (node.type.name === 'callout') {
              isInsideCallout = true
              currentCalloutType = node.attrs.type
              break
            }
          }

          // Eğer zaten aynı tip callout içindeysek, kaldır
          if (isInsideCallout && currentCalloutType === attributes.type) {
            return commands.lift(this.name)
          }

          // Eğer farklı tip callout içindeysek, önce kaldır sonra yenisini ekle
          if (isInsideCallout) {
            return chain()
              .lift(this.name)
              .wrapIn(this.name, attributes)
              .run()
          }

          // Önce wrapIn dene (basit seçimler için)
          const wrapResult = commands.wrapIn(this.name, attributes)
          if (wrapResult) {
            return true
          }

          // wrapIn başarısız olduysa, gelişmiş yöntem kullan
          // Seçili blokları bul
          const $from = state.doc.resolve(from)
          const $to = state.doc.resolve(to)

          // Seçimin kapsadığı en üst blokları bul
          const startBlock = $from.start(1)
          const endBlock = $to.end(1)

          // Seçili içeriği al
          const content: unknown[] = []
          state.doc.nodesBetween(startBlock, endBlock, (node, pos) => {
            if (pos >= startBlock && node.isBlock && pos < endBlock) {
              // Sadece en üst seviye blokları al
              const nodeDepth = state.doc.resolve(pos).depth
              if (nodeDepth === 1) {
                content.push(node.toJSON())
              }
            }
          })

          if (content.length === 0) {
            return false
          }

          // Yeni callout node oluştur ve içeriği ekle
          const calloutNode = {
            type: 'callout',
            attrs: attributes,
            content: content
          }

          // Transaction ile değiştir
          if (dispatch) {
            const newTr = tr.replaceWith(
              startBlock,
              endBlock,
              state.schema.nodeFromJSON(calloutNode)
            )
            dispatch(newTr)
          }

          return true
        },
      unsetCallout:
        () =>
        ({ commands }) => {
          return commands.lift(this.name)
        },
    }
  },

  addKeyboardShortcuts() {
    return {
      'Mod-Shift-w': () => this.editor.commands.toggleCallout({ type: 'warning' }),
      'Mod-Shift-i': () => this.editor.commands.toggleCallout({ type: 'info' }),
      'Mod-Shift-s': () => this.editor.commands.toggleCallout({ type: 'success' }),
      'Mod-Shift-e': () => this.editor.commands.toggleCallout({ type: 'error' }),
    }
  },
})

export default Callout
